import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// mantine
import { Text, Box, Divider } from '@mantine/core';
import { semanticColors } from 'theme';

// tabler icons
import {
  IconDashboard,
  IconUserCircle,
  IconBookmarks,
  IconBellRinging,
  // IconPlane, // restore with the Flight Deal Scanner menu item below
  IconAward,
  IconWorldSearch,
  IconCompass,
  IconCamera,
  IconLayoutGrid,
  IconCreditCard,
  IconUserPlus,
  IconLogout,
  IconCurrencyDollar,
  IconChartLine,
  IconTrendingUp,
  IconBuildingStore,
  IconShieldCheck,
  IconChartBar,
  IconBroadcast,
  IconIdBadge2,
  IconClipboardList,
  IconPackage,
  IconInbox,
  IconMessages,
  IconCalendar,
  IconTicket,
  IconChartDots
} from '@tabler/icons-react';

// project imports
import NavGroup from './NavGroup';
import useUserPrefix from 'hooks/use-user-prefix';
import { useHostInboxBadge } from 'hooks/useHostInboxBadge';
import { useGuestMessagesBadge } from 'hooks/useGuestMessagesBadge';
import { selectInboxBadge } from 'store/features/hostInboxSlice';
import { selectGuestUnread, selectHasGuestThreads } from 'store/features/conversationSlice';
import { useAvailabilitySetup } from 'views/host-calendar/useAvailabilitySetup';
import { BOOKING_READY_COPY } from 'views/host-calendar/calendar-copy';
import { pointsScannerEnabled } from 'utils/constants';

const COPY = {
  menuApplication: 'My Application',
  menuProfile: 'My Profile',
  // INBOX_NAV_COPY (host-inbox-tech-design §4.6); the shared badge strings live in NavItem.
  menuInbox: 'Inbox',
  // The badge counts booking requests too, so "3 new" would undersell it (host-availability UI §3.8).
  inboxBadgeSr: (n) => `${n} waiting for you`,
  menuCalendar: 'Calendar',
  menuBookings: 'Bookings',
  menuMessages: 'Messages'
};

/**
 * The host's third item follows their application (host-profile-tech-design
 * §4.2.3 a). Once approved there is nothing left to apply for, so the item
 * becomes the door to the live profile editor. The sidebar has no status fetch
 * of its own: `isApproved` rides on the login payload, and the onboarding
 * provider re-mirrors it on refresh. Keyed on the stringified boolean, so the
 * label is a lookup rather than a ternary.
 */
const HOST_ENTRY_BY_APPROVAL = {
  true: { title: COPY.menuProfile, url: '/host/profile' },
  false: { title: COPY.menuApplication, url: '/host/onboarding' }
};

// ==============================|| SIDEBAR MENU LIST ||============================== //

const MenuList = ({ drawerOpen }) => {
  const userData = useSelector((state) => state.user?.data);
  const inboxBadge = useSelector(selectInboxBadge);
  const guestUnread = useSelector(selectGuestUnread);
  const hasGuestThreads = useSelector(selectHasGuestThreads);
  const prefix = useUserPrefix();

  // Mounted here, beside the badges they feed: MainLayout renders this list
  // once, for every page in the shell. The inbox's is a no-op for anyone but
  // an approved host; the Messages badge runs for every signed-in account.
  useHostInboxBadge();
  useGuestMessagesBadge();
  // Calendar's "Set up" pill while the host's time zone is unconfirmed (host-availability U18).
  const { needsSetup } = useAvailabilitySetup();

  const userType = userData?.userType || 'member';
  // Optional chaining throughout: `userType` falls back to 'member' when there
  // is no user data at all, so every read of `userData` below has to survive
  // that same render.
  const cannotUseAppFeats = userType === 'member' && !userData?.isOTPVerified;

  /**
   * The member's conversations with hosts (host-messaging-tech-design M4). Its
   * badge counts conversations with an unread host message; NavItem hides it
   * at 0. Stays lit on /messages/1042.
   */
  const messagesItem = {
    id: 'messages',
    title: COPY.menuMessages,
    type: 'item',
    url: '/messages',
    nested: true,
    icon: IconMessages,
    badge: guestUnread
  };

  const appMenuOptions = [
    {
      id: 'apps',
      title: 'My Apps',
      type: 'item',
      url: '/my-apps',
      icon: IconDashboard,
      disabled: false
    },
    messagesItem,
    {
      id: 'profile',
      title: 'My Profile',
      type: 'item',
      url: '/profile',
      icon: IconUserCircle
    },
    {
      id: 'collection',
      title: 'My Collection',
      type: 'item',
      url: '/collection',
      icon: IconBookmarks,
      disabled: cannotUseAppFeats
    },
    {
      id: 'notification-settings',
      title: 'Notification Settings',
      type: 'item',
      url: '/notification-settings',
      icon: IconBellRinging,
      disabled: cannotUseAppFeats
    },
    // TEMPORARILY HIDDEN: Flight Deal Scanner is not functional yet. Restore when fixed.
    // {
    //   id: 'flight-deal-scanner',
    //   title: 'Flight Deal Scanner',
    //   type: 'item',
    //   url: '/flight-deal-scanner',
    //   icon: IconPlane,
    //   breadcrumbs: false,
    //   disabled: cannotUseAppFeats
    // },
    ...(pointsScannerEnabled
      ? [
          {
            id: 'points-scanner',
            title: 'Points Deal Scanner',
            type: 'item',
            url: '/points',
            icon: IconAward,
            breadcrumbs: false,
            disabled: cannotUseAppFeats
          }
        ]
      : []),
    {
      id: 'trip-planner-ai',
      title: 'Trip Planner AI',
      type: 'item',
      url: '/ai-trip-plans',
      icon: IconWorldSearch,
      disabled: cannotUseAppFeats
    },
    {
      id: 'trip-guides',
      title: 'Trip Guide Studio',
      type: 'item',
      url: '/trip-guide-studio',
      icon: IconCompass,
      disabled: cannotUseAppFeats
    },
    {
      // The public host directory (host-directory-tech-design §4.2.4). Never
      // disabled: it needs no subscription or verified account, and opens in
      // the public shell, whose "Your account" leads back here.
      id: 'host-directory',
      title: 'Find a Host',
      type: 'item',
      url: '/hosts',
      icon: IconLayoutGrid
    },
    {
      id: 'live-stream',
      title: 'Travel Hub',
      type: 'item',
      url: '/stream',
      icon: IconCamera,
      disabled: cannotUseAppFeats
    }
  ];

  const userMenuOptions = [
    {
      id: 'subscription',
      title: 'My Subscription',
      type: 'item',
      url: '/subscription',
      icon: IconCreditCard
    },
    {
      id: 'referrals',
      title: 'Refer a Friend',
      type: 'item',
      url: '/referral',
      icon: IconUserPlus
    },
    {
      id: 'logout',
      title: 'Log Out',
      type: 'item',
      url: '/',
      icon: IconLogout
    }
  ];

  const influencerMenuOptions = [
    {
      id: 'sales-dashboard',
      title: 'Sales',
      type: 'item',
      url: `/${prefix}/sales-dashboard`,
      icon: IconCurrencyDollar
    },
    {
      id: 'influencer',
      title: 'My Referrals',
      type: 'item',
      url: `/${prefix}/referrals`,
      icon: IconChartLine
    },
    {
      id: 'analytics',
      title: 'Analytics',
      type: 'item',
      url: `/${prefix}/analytics`,
      icon: IconTrendingUp
    },
    {
      id: 'invite-user',
      title: 'Add Referrals',
      type: 'item',
      url: `/${prefix}/invite-a-user`,
      icon: IconUserPlus
    },
    {
      id: 'live-stream',
      title: 'Spotlight',
      type: 'item',
      url: `/${prefix}/spotlight`,
      icon: IconBroadcast,
      disabled: cannotUseAppFeats
    }
  ];

  const stripeMenuOptions = [
    {
      id: 'stripe',
      title: 'Stripe Connect',
      type: 'item',
      url: `/${prefix}/stripe-connect-onboarding`,
      icon: IconBuildingStore
    },
    {
      id: 'balances',
      title: 'Stripe Connect Balances',
      type: 'item',
      url: `/${prefix}/stripe-connect/balances`,
      icon: IconBuildingStore
    }
  ];

  const hostEntry = HOST_ENTRY_BY_APPROVAL[String(Boolean(userData?.isApproved))];

  /**
   * Enquiries reach only an approved host with a live storefront, so the item
   * appears only once approved (host-inbox-tech-design T11). Its `badge` is
   * `needs_action`: new enquiries plus threads holding a booking request
   * (host-availability-tech-design §8.8). NavItem hides it at 0.
   */
  const hostInboxItem = {
    id: 'host-inbox',
    title: COPY.menuInbox,
    type: 'item',
    url: '/host/inbox',
    // Stays lit on /host/inbox/1042, whose first segment is only /host.
    nested: true,
    icon: IconInbox,
    badge: inboxBadge,
    badgeSr: COPY.inboxBadgeSr
  };

  /**
   * The host's calendar (host-availability-ui-design §3.10, C1): approved
   * hosts only, as the inbox. Until the host confirms a time zone it carries
   * a sand "Set up" pill (§3.11); the badge rule is unchanged for the others.
   */
  const hostCalendarItem = {
    id: 'host-calendar',
    title: COPY.menuCalendar,
    type: 'item',
    url: '/host/calendar',
    nested: true,
    icon: IconCalendar,
    pill: needsSetup ? { label: BOOKING_READY_COPY.navSetup, sr: BOOKING_READY_COPY.navSetupSr } : null
  };

  /**
   * The host's booking ledger (host-bookings-tech-design D1, §4.8): approved
   * hosts only, after the calendar. No badge: pending requests already count
   * in the Inbox's, and two badges for one piece of work would double it (T10).
   */
  const hostBookingsItem = {
    id: 'host-bookings',
    title: COPY.menuBookings,
    type: 'item',
    url: '/host/bookings',
    nested: true,
    icon: IconTicket
  };

  /**
   * A travel host's own surfaces. None of the member app features apply — a
   * host account books nothing — so this group stands alone rather than
   * extending appMenuOptions.
   *
   * Inbox comes first (D11): new enquiries are the work that cannot wait.
   * Calendar sits beside it: host work too (host-availability C1), then the
   * booking ledger (host-bookings D1). Messages
   * follows them only for a host who is also someone's guest (R6).
   */
  const hostMenuOptions = [
    ...(userData?.isApproved ? [hostInboxItem, hostCalendarItem, hostBookingsItem] : []),
    ...(hasGuestThreads ? [messagesItem] : []),
    {
      id: 'host-services',
      title: 'My Services',
      type: 'item',
      url: '/host',
      // Its siblings live under /host/*, so it must not match by first segment.
      exact: true,
      icon: IconClipboardList
    },
    {
      id: 'host-packages',
      title: 'My Packages',
      type: 'item',
      url: '/host/packages',
      icon: IconPackage
    },
    {
      // Where a host with a live package is listed. "Find a Host" reads oddly to a host.
      id: 'host-directory',
      title: 'Host Directory',
      type: 'item',
      url: '/hosts',
      icon: IconLayoutGrid
    },
    {
      id: 'host-application',
      title: hostEntry.title,
      type: 'item',
      url: hostEntry.url,
      icon: IconIdBadge2
    }
  ];

  const superAdminOptions = [
    {
      id: 'super-admin-panel',
      title: 'Admin Panel',
      type: 'item',
      url: 'admin/super-admin',
      icon: IconShieldCheck
    },
    {
      id: 'user-analytics',
      title: 'User Analytics',
      type: 'item',
      url: 'admin/user-analytics',
      icon: IconChartBar
    },
    {
      id: 'influencer-analytics',
      title: 'Influencer Analytics',
      type: 'item',
      url: 'admin/influencer-analytics',
      icon: IconChartLine
    },
    {
      id: 'creator-analytics',
      title: 'Creator Analytics',
      type: 'item',
      url: 'admin/creator-analytics',
      icon: IconChartLine
    },
    {
      id: 'host-applications',
      title: 'Host Applications',
      type: 'item',
      url: 'admin/host-applications',
      icon: IconIdBadge2
    },
    {
      // admin-host-performance-tech-design §5.7. Absolute and nested, so it
      // stays lit on a host's page under it.
      id: 'host-performance',
      title: 'Host Performance',
      type: 'item',
      url: '/admin/host-performance',
      nested: true,
      icon: IconChartDots
    }
  ];

  const appMenuItems = [
    {
      id: 'app-features',
      type: 'group',
      children: [...appMenuOptions]
    }
  ];

  const basicMenuItems = [
    {
      id: 'core-features',
      type: 'group',
      children: [...userMenuOptions]
    }
  ];

  /**
   *  Influencer menu items
   */

  const influencerStripeMenuItems = [
    {
      id: 'stripe-features',
      type: 'group',
      // Built on every render, for every user type — so it cannot assume a
      // hydrated `userData` any more than the map below can.
      children: userData?.isApproved ? [...stripeMenuOptions] : [...stripeMenuOptions.filter((item) => item.id !== 'balances')]
    }
  ];

  const influencerMenuItems = [
    {
      id: 'influencer',
      type: 'group',
      children: [...influencerMenuOptions]
    }
  ];

  const superAdminMenuItems = [
    {
      id: 'super-admin',
      type: 'group',
      children: [...superAdminOptions]
    }
  ];

  const hostMenuItems = [
    {
      id: 'host',
      type: 'group',
      children: [...hostMenuOptions]
    }
  ];

  const logoutMenuItem = [
    {
      id: 'log-out',
      type: 'group',
      children: [
        {
          id: 'logout',
          title: 'Log Out',
          type: 'item',
          url: '/',
          icon: IconLogout
        }
      ]
    }
  ];

  const menuItemsMappedByUserType = {
    user: [...appMenuItems, ...basicMenuItems],
    member: [...appMenuItems, ...basicMenuItems],
    influencer: userData?.isApproved
      ? [...influencerMenuItems, ...influencerStripeMenuItems, ...appMenuItems, ...logoutMenuItem]
      : [...influencerStripeMenuItems, ...logoutMenuItem],
    creator: userData?.isApproved
      ? [...influencerMenuItems, ...influencerStripeMenuItems, ...appMenuItems, ...logoutMenuItem]
      : [...influencerStripeMenuItems, ...logoutMenuItem],
    admin: [...superAdminMenuItems, ...appMenuItems, ...logoutMenuItem],
    // Travel hosts reach this shell from /host/welcome, which links here.
    // Without an entry they used to hit `undefined.map` and white-screen the
    // whole page.
    travel_host: [...hostMenuItems, ...logoutMenuItem]
  };

  /*
   * Falls back rather than indexing blind: a user_type the backend adds before
   * this table does must not take the entire app shell down with it. The
   * member menu is the same default `userType` already uses above.
   */
  const menuItems = menuItemsMappedByUserType[userType] ?? [...appMenuItems, ...basicMenuItems];

  const navItems = menuItems.map((item) => {
    switch (item.type) {
      case 'group':
        return <NavGroup key={item.id} item={item} opened={drawerOpen} />;
      default:
        return (
          <Text key={item.id} c="red" ta="center" size="sm">
            Menu Items Error
          </Text>
        );
    }
  });

  return (
    <Box>
      <Divider my="md" color={semanticColors.border.default} />
      {navItems}
    </Box>
  );
};

MenuList.propTypes = {
  drawerOpen: PropTypes.bool
};

export default MenuList;
